import { MemberForm } from '@/types/businesses';
import {
  Controller,
  UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';
import { SVAutocomplete, SVButton, SVTextInput } from '../core';
import {
  Box,
  FormLabel,
  IconButton,
  Radio,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useGetCustomerListForSelection } from '@/hooks/requests/useCustomers';
import { defaultColorPalette } from '@/constants/brandingThemes';
import { REGEX } from '@/constants/regex';
import { blankBusinessMember } from '@/constants/business';
import { ReactElement } from 'react';

type BusinessMembersFormProps = {
  companyId: string;
  advisorIds: string[];
  submitButton?: ReactElement;
  setOpenAddCustomerModal: (value: React.SetStateAction<boolean>) => void;
  fieldArray: UseFieldArrayReturn<any, 'members', 'id'>;
};

export default function BusinessMembersForm({
  companyId = '',
  advisorIds = [],
  submitButton,
  setOpenAddCustomerModal,
  fieldArray,
}: BusinessMembersFormProps) {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const { customerListByBoth, isCustomerListPending } =
    useGetCustomerListForSelection({
      company_id: companyId,
      teammate_ids: advisorIds,
    });

  const formMethods = useFormContext<MemberForm>();
  const { control, setValue, getValues } = formMethods;
  const { fields, append, remove } = fieldArray;

  if (isCustomerListPending) {
    return null;
  }

  const containerStyling = {
    display: 'flex',
    flexDirection: isLargeScreen ? 'row' : 'column',
    gap: 2,
    mb: 1,
  };
  const ownershipColumn = isLargeScreen ? '120px' : '100%';
  const titleColumn = isLargeScreen ? '150px' : '100%';
  const controlColumn = isLargeScreen ? '140px' : '100%';
  const removeColumn = isLargeScreen ? '50px' : 'auto';
  const fontWeight = 'bold';

  function handleRadioChange(selectIndex: number) {
    getValues().members.forEach((_member, index) => {
      setValue(`members.${index}.isControlPerson`, index === selectIndex);
    });
  }

  return (
    <>
      {customerListByBoth.length === 0 && (
        <Typography color="error">
          The advisor{advisorIds.length > 1 ? 's have' : ' has'} no customer
          associated to them. Please add a new customer with the button below.
        </Typography>
      )}
      {isLargeScreen && (
        <Box sx={containerStyling}>
          <Typography sx={{ fontWeight, flex: 1 }}>Name</Typography>
          <Typography sx={{ fontWeight, width: ownershipColumn }}>
            Ownership
          </Typography>
          <Typography sx={{ fontWeight, width: titleColumn }}>Title</Typography>
          <Typography sx={{ fontWeight, width: controlColumn }}>
            Control Person
          </Typography>
          <Typography sx={{ fontWeight, width: removeColumn }}></Typography>
        </Box>
      )}
      {fields.map((item, index) => (
        <Box
          data-testid={`membersRow${index}`}
          key={item.id}
          sx={{
            ...containerStyling,
            borderBottom: isLargeScreen
              ? 'none'
              : `1px solid ${defaultColorPalette.border}`,
            mb: isLargeScreen ? 0 : 2,
          }}>
          <Box sx={{ flex: 1 }}>
            <SVAutocomplete
              name={`members.${index}.consumer`}
              label="Name"
              options={customerListByBoth}
              hideLabel={isLargeScreen}
              rules={{ required: true }}
            />
          </Box>
          <Box sx={{ width: ownershipColumn }}>
            <SVTextInput
              name={`members.${index}.ownershipPercentage`}
              label="Ownership Percentage"
              hideLabel={isLargeScreen}
              inputProps={{ endAdornment: <Typography>%</Typography> }}
              rules={{
                required: true,
                pattern: {
                  regex: REGEX.DECIMAL_NUMBER,
                  readablePatternName: 'percentage',
                },
                max: 100,
              }}
            />
          </Box>
          <Box sx={{ width: titleColumn }}>
            <SVTextInput
              name={`members.${index}.title`}
              label="Title"
              hideLabel={isLargeScreen}
              rules={{ required: true }}
            />
          </Box>
          <Controller
            control={control}
            name={`members.${index}.isControlPerson`}
            render={({ field: { value } }) => (
              <Box
                sx={{
                  width: controlColumn,
                  textAlign: isLargeScreen ? 'center' : 'left',
                }}>
                <Radio
                  checked={value}
                  onChange={() => handleRadioChange(index)}
                  value={value}
                />
                {!isLargeScreen && <FormLabel>Is Control Person</FormLabel>}
              </Box>
            )}
          />
          {fields.length >= 2 ? (
            <IconButton
              data-testid={`membersRowDelete${index}`}
              sx={{ width: removeColumn, alignSelf: 'flex-start' }}
              onClick={() => remove(index)}>
              <Delete />
            </IconButton>
          ) : (
            <Box width={50} />
          )}
        </Box>
      ))}
      {fields.length < 7 && (
        <SVButton
          fullWidth
          data-testid="add-business-member-button"
          size="small"
          variant="tertiary"
          onClick={() => append(blankBusinessMember)}
          sx={{ mb: 2, height: 30 }}>
          <Add sx={{ fontSize: 20 }} />
        </SVButton>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <SVButton
          variant="outlined"
          onClick={() => setOpenAddCustomerModal(true)}>
          Add New Customer
        </SVButton>
        {submitButton && <>{submitButton}</>}
      </Box>
    </>
  );
}
